import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { createApiClient, apiClient,getLocalTimestamp } from "../../api/apiClient";
import { handleNHOErrorWithFriendlyMessage, NHO_API_NAMES } from '../utils/nhoErrorHandler';

// Pulse Check Data Interface
export interface PulseCheckMetric {
  Id: number;
  DPS_Target: number;
  CPH_Target: number;
  Gap_Time_Target: number;
  SIA_Target: number;
  Attendance: number;
  Week: string;
  DPS_Actual: number;
  CPH_Actual: number;
  Gap_Time_Actual: number;
  SIA_Actual: number;
  Period: number;
  Pepsi_Week: number;
  is_actual: boolean;
  is_updated: boolean;
}

export interface PulseCheckData {
  user: {
    First_Name: string;
    Last_Name: string;
    Peer_Coach: string | null;
    Expected_Graduation_Date: string;
    Weeks_till_graduation: number | null;
    Comments1?: string; // Add Comments1 for observation notes in user object
  };
  metrics: PulseCheckMetric[];
  attendance: any[];
  observations?: string;
  Comments1?: string; // Add Comments1 for observation notes from API
  employee_signature?: string;
  manager_signature?: string;
  notification?: {
    type: string;
    message: string;
    requestedWeeks?: number;
    availableWeeks?: number;
    displayedWeeks?: number;
  };
}

// Types - Store complete API response data
export interface NHOEmployeeData {
  ID?: number;
  Location_ID?: number;
  Location_Name?: string;
  GPID?: string;
  First_Name?: string;
  Last_Name?: string;
  Role?: string;
  Active_vs_Inactive?: string;
  Created?: string;
  Modified?: string;
  Last_Pulse_Check?: string;
  Last_Observation?: string;
  Week?: number;
  Period?: number;
  Current_Week?: number;
  Peer_Coach?: string;
  New_Hire?: number;
  Joining_Date?: string;
  Expected_Graduation_Date?: string;
  Weeks_till_graduation?: number;
  Hire_Date?: string;
  Manager?: string;
  Last_Observation_Date?: string;
  Last_Pulse_Check_Date?: string;
  // Computed fields for convenience
  Full_Name?: string;
  Employee_Status?: string;
  Location_ID_String?: string;
}

// API Employee Response Type
export interface NHOEmployeeApiResponse {
  ID?: number;
  Location_ID?: number;
  Location_Name?: string;
  GPID?: string;
  First_Name?: string;
  Last_Name?: string;
  Role?: string;
  Active_vs_Inactive?: string;
  Created?: string;
  Modified?: string;
  Last_Pulse_Check?: string;
  Last_Observation?: string;
  Week?: number;
  Period?: number;
  Current_Week?: number;
  Peer_Coach?: string;
  New_Hire?: number;
  Joining_Date?: string;
  Expected_Graduation_Date?: string;
  Weeks_till_graduation?: number;
  Hire_Date?: string;
  Manager?: string;
  Last_Observation_Date?: string;
  Last_Pulse_Check_Date?: string;
}

export interface NHOObservationType {
  id: string;
  name: string;
  description?: string;
}

export interface FilterItem {
  key: string;
  label: string;
  options: { id: string; displayText: string }[];
}

export interface NHOTouchpoint {
  Id: number;
  Probation_Id: string;
  Type_of_Touchpoint: string;
  Employee_Name: string;
  Created: string;
  Created_By: string;
  GPID: string;
  Location_Name: string;
  Coach_Name: string | null;
  Comments1: string | null;
  Comments2: string | null;
  Status: string;
  Period_Week: string;
  Period: number | null;
  Week: number | null;
  Pepsi_Week: number | null;
  Emp_Sign_Status: number | null;
  [key: string]: any; // Allow additional properties
}

// TouchPoint Survey State Management Interfaces
export type SurveyAnswer =
  | "Yes"
  | "No"
  | "N/A"
  | "Good"
  | "Needs Improvement"
  | "Above Target/On Target"
  | "Below Target"
  | null;

export interface TouchPointSurveyForm {
  surveyAnswers: Record<string, SurveyAnswer>;
  comments: Record<string, string>;
  employeeSignature: {
    fileName: string | null;
    imageUrl: string | null;
  };
  managerSignature: {
    fileName: string | null;
    imageUrl: string | null;
  };
  isEmployeeDisclaimerChecked: boolean;
  isComplete: boolean;
  lastUpdated: string | null;
}

export interface TouchPointSurveyState {
  currentForm: TouchPointSurveyForm;
  submissionStatus: {
    isSubmitting: boolean;
    error: string | null;
    success: boolean;
  };
}

interface CentralizedError {
  id: string;
  apiName: string;
  message: string;
  timestamp: number;
  context?: any;
}

interface NHOState {
  employees: NHOEmployeeData[];
  observationTypes: NHOObservationType[];
  selectedFilters: {
    employee: string;
    employeeFirstName?: string;
    employeeLastName?: string;
    employeeGpid?: string;
    observationType: string;
    touchpoint?: string;
    touchpointId?: number;
    periodWeek?: string;
    period?: string;
    Period?: string;
    Week?: string;
    year?: string;
    createdBy?: string;
    createdByName?: string;
    status?: string;
  };
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  isModalVisible: boolean;
  // New properties for touchpoints table
  touchpoints: NHOTouchpoint[];
  filteredTouchpoints: NHOTouchpoint[];
  // Employee loading states
  employeeLoading: boolean;
  employeeLoadError: string | null;
  // Pulse check data
  pulseCheckData: PulseCheckData | null;
  pulseCheckLoading: boolean;
  pulseCheckError: string | null;
  // Add observationNotes to state
  observationNotes: string;
  // TouchPoint Survey state
  touchPointSurvey: TouchPointSurveyState;
  // Pulse check notification
  pulseCheckNotification: {
    type: string;
    message: string;
    requestedWeeks?: number;
    availableWeeks?: number;
    displayedWeeks?: number;
  } | null;
  // Centralized error handling (common pattern used in other modules)
  centralizedErrors: CentralizedError[];
  isErrorModalVisible: boolean;
}

// Async thunk to fetch employees for NHO Dashboard
export const fetchNHOEmployees = createAsyncThunk(
  "nho/fetchEmployees",
  async (locationName: string, { rejectWithValue }) => {
    try {
      const apiClient = createApiClient();
      const response = await apiClient.get(
        `/nho/employees?Location_Name=${encodeURIComponent(
          locationName
        )}&Is_New_Hire=true&Active_vs_Inactive=Active`
      );

      if (response.status === 200) {
        return response.data as NHOEmployeeApiResponse[];
      } else {
        throw new Error(`API returned status ${response.status}`);
      }
    } catch (error: any) {
      console.error("Error fetching NHO employees:", error);
      try {
        handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.FETCH_EMPLOYEES, error, true, { locationName });
      } catch (e) {
        console.error('Error while dispatching NHO centralized handler for fetchNHOEmployees:', e);
      }
      let errorMessage = "Failed to fetch employees";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk to fetch pulse check data
export const fetchPulseCheckData = createAsyncThunk(
  "nho/fetchPulseCheckData",
  async (params: { gpid: string; location: string }, { rejectWithValue }) => {
    try {
      const api = createApiClient();
      const url = `/new-hire-onboarding/pulse-check?gpid=${
        params.gpid
      }&location=${encodeURIComponent(params.location)}`;
    
      const response = await apiClient(api, url, "GET");

      // Validate response - if API returns no data or unexpected shape, show overlay
      if (!response || typeof response !== 'object' || !response.user) {
        const userFacingError = 'Unable to load pulse check data. Please try again.';
        try {
          handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.FETCH_PULSE_CHECK, userFacingError, true, { params });
        } catch (e) {
          console.error('Failed to dispatch NHO centralized handler for invalid pulse check response:', e);
        }
        return rejectWithValue(userFacingError);
      }

      return response as PulseCheckData;
    } catch (error: any) {
      console.error("Error fetching pulse check data:", error);
      try {
        handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.FETCH_PULSE_CHECK, error, true, { params });
      } catch (e) {
        console.error('Error while dispatching NHO centralized handler for fetchPulseCheckData:', e);
      }
      let errorMessage = "Failed to fetch pulse check data";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

// Interface for touchpoint submission payload
export interface TouchPointSubmissionPayload {
  surveyAnswers: Record<string, SurveyAnswer>;
  comments: Record<string, string>;
  employeeSignature: {
    fileName: string | null;
    imageUrl: string | null;
  };
  managerSignature: {
    fileName: string | null;
    imageUrl: string | null;
  };
  observationType: string;
  employeeGpid?: string;
  locationName?: string;
  employeeName?: string;
  surveyItems?: Array<{
    id: string;
    sectionValue: string;
    sectionType: string;
    section: number;
  }>;
  status?: string; // Add status field for "In Progress" or "Completed"
  observations?: string;
  additional_observations?: string;
}

// Utility function to format user name from "LastName, FirstName - Title" to "FirstName LastName"
const formatUserName = (fullName: string): string => {
  if (!fullName) {
    return "";
  }

  // Split by comma to get the lastname and the rest
  const parts = fullName.split(",");
  if (parts.length < 2) {
    return fullName;
  } // Return as-is if format is unexpected

  const lastName = parts[0].trim();
  const rest = parts[1].trim();

  // Extract first name (everything before " - " or just the whole thing if no " - ")
  const dashIndex = rest.indexOf(" - ");
  const firstName =
    dashIndex !== -1 ? rest.substring(0, dashIndex).trim() : rest.trim();

  return `${firstName} ${lastName}`;
};

// Async thunk to submit touchpoint survey
export const submitTouchPointSurvey = createAsyncThunk(
  "nho/submitTouchPointSurvey",
  async (
    payload: TouchPointSubmissionPayload,
    { rejectWithValue, getState }
  ) => {
    try {
      const api = createApiClient();
      const url = "/new-hire-onboarding/probation";

      // Get state to access required information
      const state = getState() as any;
      const { selectedFilters } = state.nho;
      const { selectedLocation } = state.appData || {};
      const { user } = state.auth || {};

      // Get GPID from selectedFilters
      const gpid = selectedFilters.employeeGpid || "";

      // Get Employee_Name from selectedFilters first/last name fields, falling back to employee (name)
      const employeeName =
        [selectedFilters.employeeFirstName, selectedFilters.employeeLastName]
          .filter(Boolean)
          .join(" ") || selectedFilters.employee || "";

      // Get Location_Name from appData.selectedLocation.displayText
      const locationName = selectedLocation?.displayText || "";

      // Get and format createdBy from auth.user.name
      const userName = user?.username || "";
      const formattedCreatedBy = userName;

      // Format typeOfTouchpoint based on observationType
      let typeOfTouchpoint = "";
      switch (payload.observationType) {
        case "preferred_method":
          typeOfTouchpoint = "Preferred Method";
          break;
        case "30":
          typeOfTouchpoint = "Probation 30";
          break;
        case "60":
          typeOfTouchpoint = "Probation 60";
          break;
        case "90":
          typeOfTouchpoint = "Probation 90";
          break;
        default:
          typeOfTouchpoint = `Probation ${payload.observationType}`;
      }

      // Create a map of survey items for quick lookup
      const surveyItemsMap = payload.surveyItems
        ? payload.surveyItems.reduce((map, item) => {
            map[item.id] = item;
            return map;
          }, {} as Record<string, any>)
        : {};

      // Transform survey answers to probationData format
      const probationData = Object.entries(payload.surveyAnswers).map(
        ([dataId, answer]) => {
          const surveyItem = surveyItemsMap[dataId];
          const sectionId = surveyItem?.section || 1; // Use section ID, default to 1 if not found

          // Map answers based on section type
          let responseValue = 0; // Default value

          if (payload.observationType === "preferred_method") {
            // Preferred method always uses Yes/No/N/A
            responseValue =
              answer === "Yes"
                ? 1
                : answer === "No"
                ? 2
                : answer === "N/A"
                ? 3
                : 0;
          } else {
            // Map based on section
            switch (sectionId) {
              case 1:
                // Section 1: Yes/No/N/A
                responseValue =
                  answer === "Yes"
                    ? 1
                    : answer === "No"
                    ? 2
                    : answer === "N/A"
                    ? 3
                    : 0;
                break;
              case 2:
                // Section 2: Yes/No/N/A
                responseValue =
                  answer === "Yes"
                    ? 1
                    : answer === "No"
                    ? 2
                    : answer === "N/A"
                    ? 3
                    : 0;
                break;
              case 3:
                // Section 3: Above Target/On Target/Below Target/N/A
                responseValue =
                  answer === "Above Target/On Target"
                    ? 1
                    : answer === "Below Target"
                    ? 2
                    : answer === "N/A"
                    ? 3
                    : 0;
                break;
              default:
                // Default to Yes/No/N/A
                responseValue =
                  answer === "Yes"
                    ? 1
                    : answer === "No"
                    ? 2
                    : answer === "N/A"
                    ? 3
                    : 0;
                break;
            }
          }

          return {
            Data_Id: parseInt(dataId),
            Response: responseValue,
            Section: sectionId,
          };
        }
      );

      // Separate comments by section
      const section1Comment = payload.comments["section-1"] || "";
      const section2Comment = payload.comments["section-2"] || "";
      const section3Comment = payload.comments["section-3"] || "";
      const singleComment = payload.comments["single-comment"] || "";

      // For preferred method, use single comment as observations
      // For other touchpoints, use section 1 comment as observations and section 2 as additional_observations
      let observations = "";
      let additional_observations = "";

      if (payload.observationType === "preferred_method") {
        observations = singleComment.trim();
      } else {
        observations = section1Comment.trim();
        additional_observations = section2Comment.trim();
      }

      // Helper function to get full URL for signatures
      const getFullSignatureUrl = (signature: {
        fileName: string | null;
        imageUrl: string | null;
      }) => {
        if (signature.imageUrl) {
          // If imageUrl is already a full URL, return it
          if (
            signature.imageUrl.startsWith("http://") ||
            signature.imageUrl.startsWith("https://")
          ) {
            return signature.imageUrl;
          }
          // If it's a relative path, you might need to prepend the base URL
          // return `${process.env.REACT_APP_BASE_URL || ''}${signature.imageUrl}`;
          return signature.imageUrl;
        }
        return signature.fileName || "";
      };

      // Construct API payload according to schema
      const apiPayload = {
        GPID: gpid ? gpid : 0,
        Location_Name: locationName,
        Employee_Name: employeeName,
        createdBy: formattedCreatedBy,
        typeOfTouchpoint: typeOfTouchpoint,
        probationData: probationData,
        observations: observations,
        additional_observations: additional_observations,
        status: payload.status || "Completed", // Use provided status or default to 'Completed'
        manager_signature: getFullSignatureUrl(payload.managerSignature),
        employee_signature: getFullSignatureUrl(payload.employeeSignature),
      };

      console.log("Submitting touchpoint survey with payload:", apiPayload);
      console.log("Comment separation debug:", {
        originalComments: payload.comments,
        section1Comment,
        section3Comment,
        singleComment,
        finalObservations: observations,
        finalAdditionalObservations: additional_observations,
        submissionStatus: payload.status,
        observationType: payload.observationType,
      });
      console.log(
        "ProbationData details:",
        probationData.map((item) => ({
          Data_Id: item.Data_Id,
          Response: item.Response,
          Section: item.Section,
          SurveyItem: surveyItemsMap[item.Data_Id.toString()],
        }))
      );

      const response = await apiClient(api, url, "POST", apiPayload);

      return response;
    } catch (error: any) {
      console.error("Error submitting touchpoint survey:", error);
      try {
        handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.SUBMIT_TOUCHPOINT, error, true, { payload });
      } catch (e) {
        console.error('Error while dispatching NHO centralized handler for submitTouchPointSurvey:', e);
      }
      console.error("Error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });

      let errorMessage = "Failed to submit touchpoint survey";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 500) {
        errorMessage =
          "Server error occurred while processing your request. Please try again later.";
      } else if (error.response?.status === 400) {
        errorMessage =
          "Invalid data submitted. Please check your form and try again.";
      } else if (
        error.response?.status === 401 ||
        error.response?.status === 403
      ) {
        errorMessage = "You are not authorized to perform this action.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

// Add new thunk for submitting pulse check data
export const submitPulseCheck = createAsyncThunk(
  "nho/submitPulseCheck",
  async (
    data: {
      GPID: string;
      Location_Name: string;
      metrics: Array<{
        DPS_Target: number;
        CPH_Target: number;
        GAP_Time_Target: number;
        SIA_Target: number;
        Attendance: number;
        Week: string;
        DPS_Actual: number;
        CPH_Actual: number;
        Gap_Time_Actual: number;
        SIA_Actual: number;
        Period: number;
        Pepsi_Week: number;
      }>;
      attendance: Array<{
        Date_of_Absence: string;
        Points: number;
      }>;
      observations: string;
      manager_signature: string;
      employee_signature: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const api = createApiClient();
      const response = await apiClient(
        api,
        "/new-hire-onboarding/pulse-check",
        "POST",
        data
      );
      return response.data;
    } catch (error: any) {
      console.error("Error submitting pulse check:", error);
      try {
        handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.SUBMIT_PULSE_CHECK, error, true, { data });
      } catch (e) {
        console.error('Error while dispatching NHO centralized handler for submitPulseCheck:', e);
      }
      return rejectWithValue(error.message || "Failed to submit pulse check");
    }
  }
);

const initialState: NHOState = {
  employees: [],
  observationTypes: [
    { id: "onboarding", name: "Onboarding" },
    { id: "training", name: "Training" },
    { id: "milestone1", name: "Milestone 1" },
    { id: "milestone2", name: "Milestone 2" },
    { id: "milestone3", name: "Milestone 3" },
  ],
  selectedFilters: {
    employee: "",
    observationType: "",
    touchpoint: "",
    touchpointId: undefined,
    periodWeek: "",
    period: "",
    Period: "",
    Week: "",
    year: "",
    createdBy: "",
    createdByName: "",
    status: "",
  },
  loading: false,
  error: null,
  successMessage: null,
  isModalVisible: false,
  // Initialize the touchpoints arrays
  touchpoints: [],
  filteredTouchpoints: [],
  // Initialize employee loading states
  employeeLoading: false,
  employeeLoadError: null,
  // Initialize pulse check data
  pulseCheckData: null,
  pulseCheckLoading: false,
  pulseCheckError: null,
  // Initialize observationNotes
  observationNotes: "",
  // Initialize pulse check notification
  pulseCheckNotification: null,
  // Initialize touchpoint survey state
  touchPointSurvey: {
    currentForm: {
      surveyAnswers: {},
      comments: {},
      employeeSignature: {
        fileName: null,
        imageUrl: null,
      },
      managerSignature: {
        fileName: null,
        imageUrl: null,
      },
      isEmployeeDisclaimerChecked: false,
      isComplete: false,
      lastUpdated: null,
    },
    submissionStatus: {
      isSubmitting: false,
      error: null,
      success: false,
    },
  },
  // Initialize centralized error handling
  centralizedErrors: [],
  isErrorModalVisible: false,
};

const nhoSlice = createSlice({
  name: "nho",
  initialState,
  reducers: {
    setEmployees: (state, action: PayloadAction<NHOEmployeeData[]>) => {
      state.employees = action.payload;
    },
    setObservationTypes: (
      state,
      action: PayloadAction<NHOObservationType[]>
    ) => {
      state.observationTypes = action.payload;
    },
    setSelectedFilters: (
      state,
      action: PayloadAction<{
        employee?: string;
        employeeFirstName?: string;
        employeeLastName?: string;
        employeeGpid?: string;
        observationType?: string;
        touchpoint?: string;
        touchpointId?: number;
        periodWeek?: string;
        period?: string;
        Period?: string;
        Week?: string;
        year?: string;
        createdBy?: string;
        createdByName?: string;
        status?: string;
      }>
    ) => {
      state.selectedFilters = {
        ...state.selectedFilters,
        ...action.payload,
      };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setSuccessMessage: (state, action: PayloadAction<string | null>) => {
      state.successMessage = action.payload;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    setModalVisibility: (state, action: PayloadAction<boolean>) => {
      state.isModalVisible = action.payload;
    },
    // New reducers for touchpoints
    setTouchpoints: (state, action: PayloadAction<NHOTouchpoint[]>) => {
      state.touchpoints = action.payload;
    },
    setFilteredTouchpoints: (state, action: PayloadAction<NHOTouchpoint[]>) => {
      state.filteredTouchpoints = action.payload;
    },
    resetFilters: (state) => {
      state.selectedFilters = {
        employee: "",
        observationType: "",
        touchpoint: "",
        touchpointId: undefined,
        periodWeek: "",
        period: "",
        Period: "",
        Week: "",
        year: "",
        createdBy: "",
        createdByName: "",
        status: "",
      };
    },
    // Clear employee load error
    clearEmployeeLoadError: (state) => {
      state.employeeLoadError = null;
    },
    // Pulse check data actions
    setPulseCheckData: (
      state,
      action: PayloadAction<PulseCheckData | null>
    ) => {
      state.pulseCheckData = action.payload;
      // Always use Comments1 for observation notes (prefer user.Comments1 if present)
      state.observationNotes =
        action.payload?.user?.Comments1 || action.payload?.Comments1 || "";
      // Set notification if present in the response
      state.pulseCheckNotification = action.payload?.notification || null;
    },
    setPulseCheckLoading: (state, action: PayloadAction<boolean>) => {
      state.pulseCheckLoading = action.payload;
    },
    setPulseCheckError: (state, action: PayloadAction<string | null>) => {
      state.pulseCheckError = action.payload;
    },
    clearPulseCheckError: (state) => {
      state.pulseCheckError = null;
    },
    setPulseCheckNotification: (
      state,
      action: PayloadAction<{
        type: string;
        message: string;
        requestedWeeks?: number;
        availableWeeks?: number;
        displayedWeeks?: number;
      } | null>
    ) => {
      state.pulseCheckNotification = action.payload;
    },
    clearPulseCheckNotification: (state) => {
      state.pulseCheckNotification = null;
    },
    // Add observationNotes actions
    setObservationNotes: (state, action: PayloadAction<string>) => {
      state.observationNotes = action.payload;
      // Also update pulseCheckData.observations if pulseCheckData exists
      if (state.pulseCheckData) {
        state.pulseCheckData.observations = action.payload;
      }
    },
    // TouchPoint Survey reducers
    setTouchPointSurveyAnswers: (
      state,
      action: PayloadAction<Record<string, SurveyAnswer>>
    ) => {
      state.touchPointSurvey.currentForm.surveyAnswers = action.payload;
      state.touchPointSurvey.currentForm.lastUpdated = getLocalTimestamp()
    },
    updateTouchPointSurveyAnswer: (
      state,
      action: PayloadAction<{ questionId: string; answer: SurveyAnswer }>
    ) => {
      state.touchPointSurvey.currentForm.surveyAnswers[
        action.payload.questionId
      ] = action.payload.answer;
      state.touchPointSurvey.currentForm.lastUpdated = getLocalTimestamp()
    },
    setTouchPointSurveyComments: (
      state,
      action: PayloadAction<Record<string, string>>
    ) => {
      state.touchPointSurvey.currentForm.comments = action.payload;
      state.touchPointSurvey.currentForm.lastUpdated = getLocalTimestamp()
    },
    updateTouchPointSurveyComment: (
      state,
      action: PayloadAction<{ sectionId: string; comment: string }>
    ) => {
      state.touchPointSurvey.currentForm.comments[action.payload.sectionId] =
        action.payload.comment;
      state.touchPointSurvey.currentForm.lastUpdated = getLocalTimestamp()
    },
    setTouchPointEmployeeSignature: (
      state,
      action: PayloadAction<{
        fileName: string | null;
        imageUrl: string | null;
      }>
    ) => {
      state.touchPointSurvey.currentForm.employeeSignature = action.payload;
      state.touchPointSurvey.currentForm.lastUpdated = getLocalTimestamp()
    },
    setTouchPointManagerSignature: (
      state,
      action: PayloadAction<{
        fileName: string | null;
        imageUrl: string | null;
      }>
    ) => {
      state.touchPointSurvey.currentForm.managerSignature = action.payload;
      state.touchPointSurvey.currentForm.lastUpdated = getLocalTimestamp()
    },
    setTouchPointSurveyComplete: (state, action: PayloadAction<boolean>) => {
      state.touchPointSurvey.currentForm.isComplete = action.payload;
      state.touchPointSurvey.currentForm.lastUpdated = getLocalTimestamp()
    },
    setTouchPointEmployeeDisclaimerChecked: (state, action: PayloadAction<boolean>) => {
      state.touchPointSurvey.currentForm.isEmployeeDisclaimerChecked = action.payload;
      state.touchPointSurvey.currentForm.lastUpdated = getLocalTimestamp()
    },
    resetTouchPointSurveyForm: (state) => {
      state.touchPointSurvey.currentForm = {
        surveyAnswers: {},
        comments: {},
        employeeSignature: {
          fileName: null,
          imageUrl: null,
        },
        managerSignature: {
          fileName: null,
          imageUrl: null,
        },
        isEmployeeDisclaimerChecked: false,
        isComplete: false,
        lastUpdated: null,
      };
      state.touchPointSurvey.submissionStatus = {
        isSubmitting: false,
        error: null,
        success: false,
      };
        state.pulseCheckData = null; // Clear pulseCheckData when resetting survey
        state.observationNotes = ""; // Clear observation notes when resetting survey
        state.pulseCheckNotification = null; // Clear notification when resetting survey
        state.selectedFilters = {
          employee: "",
          observationType: "",
          touchpoint: "",
        };
    },
    clearTouchPointSubmissionStatus: (state) => {
      state.touchPointSurvey.submissionStatus = {
        isSubmitting: false,
        error: null,
        success: false,
      };
    },
    updateMetrics: (state, action: PayloadAction<PulseCheckMetric[]>) => {
      if (!state.pulseCheckData?.metrics || !Array.isArray(action.payload)) {
        return;
      }

      // Deep compare metrics to check for actual changes
      const hasMetricChanged = (
        newMetric: PulseCheckMetric,
        existingMetric: PulseCheckMetric
      ) => {
        if (!existingMetric || !newMetric) {
          return false;
        }

        const fieldsToCompare = [
          "DPS_Actual",
          "CPH_Actual",
          "Gap_Time_Actual",
          "SIA_Actual",
          "Period",
          "Pepsi_Week",
        ];

        return fieldsToCompare.some(
          (field) =>
            newMetric[field as keyof PulseCheckMetric] !==
            existingMetric[field as keyof PulseCheckMetric]
        );
      };

      // Find metrics that have actual changes
      const changedMetrics = action.payload.filter((newMetric) => {
        const existingMetric = state.pulseCheckData!.metrics.find(
          (m) => m.Week === newMetric.Week
        );
        return hasMetricChanged(newMetric, existingMetric!);
      });

      // Only update if we have actual changes
      if (changedMetrics.length === 0) {
        return;
      }

      // Update only the changed metrics while preserving other fields
      state.pulseCheckData.metrics = state.pulseCheckData.metrics.map(
        (existingMetric) => {
          const updatedMetric = changedMetrics.find(
            (m) => m.Week === existingMetric.Week
          );
          if (updatedMetric) {
            return {
              ...existingMetric,
              ...updatedMetric,
            };
          }
          return existingMetric;
        }
      );
    },
    updateAttendance: (
      state,
      action: PayloadAction<
        Array<{
          Date_of_Absence: string;
          Points: number;
        }>
      >
    ) => {
      if (state.pulseCheckData) {
        state.pulseCheckData.attendance = action.payload;
        console.log("Updated attendance in Redux store:", action.payload);
      }
    },
    updateObservations: (state, action: PayloadAction<string>) => {
      if (state.pulseCheckData) {
        state.pulseCheckData.Comments1 = action.payload; // Always update Comments1 for last submitted value
      }
      state.observationNotes = action.payload;
      console.log("Updated Comments1 in Redux store:", action.payload);
    },
    // Centralized error handling reducers
    addCentralizedError: (state, action: PayloadAction<CentralizedError>) => {
      state.centralizedErrors.push(action.payload);
      // keep last 10
      if (state.centralizedErrors.length > 10) {
        state.centralizedErrors = state.centralizedErrors.slice(-10);
      }
    },
    clearCentralizedErrors: (state) => {
      state.centralizedErrors = [];
    },
    setErrorModalVisible: (state, action: PayloadAction<boolean>) => {
      state.isErrorModalVisible = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNHOEmployees.pending, (state) => {
        state.employeeLoading = true;
        state.employeeLoadError = null;
      })
      .addCase(fetchNHOEmployees.fulfilled, (state, action) => {
        state.employeeLoading = false;
        state.employeeLoadError = null;
        // Store complete API response with computed convenience fields
        state.employees = action.payload.map((emp: NHOEmployeeApiResponse) => ({
          // Store all original API fields
          ...emp,
          // Add computed convenience fields
          Full_Name: `${emp.First_Name || ""} ${emp.Last_Name || ""}`.trim(),
          Employee_Status: emp.Active_vs_Inactive || "",
          Location_ID_String: emp.Location_ID?.toString() || "",
        }));
      })
      .addCase(fetchNHOEmployees.rejected, (state, action) => {
        state.employeeLoading = false;
        state.employeeLoadError = action.payload as string;
        state.employees = []; // Clear employees on error
      })
      // Handle fetchPulseCheckData
      .addCase(fetchPulseCheckData.pending, (state) => {
        state.pulseCheckLoading = true;
        state.pulseCheckError = null;
      })
      .addCase(fetchPulseCheckData.fulfilled, (state, action) => {
        // Use the setPulseCheckData reducer to sync observationNotes with API response
        nhoSlice.caseReducers.setPulseCheckData(state, action);
        state.pulseCheckLoading = false;
        state.pulseCheckError = null;
      })
      .addCase(fetchPulseCheckData.rejected, (state, action) => {
        state.pulseCheckLoading = false;
        state.pulseCheckError = action.payload as string;
        state.pulseCheckData = null;
      })
      // Handle submitTouchPointSurvey
      .addCase(submitTouchPointSurvey.pending, (state) => {
        state.touchPointSurvey.submissionStatus.isSubmitting = true;
        state.touchPointSurvey.submissionStatus.error = null;
        state.touchPointSurvey.submissionStatus.success = false;
      })
      .addCase(submitTouchPointSurvey.fulfilled, (state) => {
        state.touchPointSurvey.submissionStatus.isSubmitting = false;
        state.touchPointSurvey.submissionStatus.success = true;
        state.touchPointSurvey.submissionStatus.error = null;
        // Optionally reset the form after successful submission
        // state.touchPointSurvey.currentForm = initialState.touchPointSurvey.currentForm;
      })
      .addCase(submitTouchPointSurvey.rejected, (state, action) => {
        state.touchPointSurvey.submissionStatus.isSubmitting = false;
        state.touchPointSurvey.submissionStatus.error =
          action.payload as string;
        state.touchPointSurvey.submissionStatus.success = false;
      })
      // Handle pulse check submission
      .addCase(submitPulseCheck.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitPulseCheck.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.successMessage = "Pulse check submitted successfully";
        // Reset pulseCheckData after successful submission to clear stale/deleted attendance
        state.pulseCheckData = null;
      })
      .addCase(submitPulseCheck.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setEmployees,
  setObservationTypes,
  setSelectedFilters,
  setLoading,
  setError,
  setSuccessMessage,
  clearSuccessMessage,
  setModalVisibility,
  resetFilters,
  // New actions
  setTouchpoints,
  setFilteredTouchpoints,
  clearEmployeeLoadError,
  // Pulse check actions
  setPulseCheckData,
  setPulseCheckLoading,
  setPulseCheckError,
  clearPulseCheckError,
  setPulseCheckNotification,
  clearPulseCheckNotification,
  // TouchPoint Survey actions
  setTouchPointSurveyAnswers,
  updateTouchPointSurveyAnswer,
  updateAttendance,
  setTouchPointSurveyComments,
  updateTouchPointSurveyComment,
  setTouchPointEmployeeSignature,
  setTouchPointManagerSignature,
  setTouchPointSurveyComplete,
  setTouchPointEmployeeDisclaimerChecked,
  resetTouchPointSurveyForm,
  clearTouchPointSubmissionStatus,
  updateMetrics,
  updateObservations,
  // Observation notes action
  setObservationNotes,
  // Centralized error actions
  addCentralizedError,
  clearCentralizedErrors,
  setErrorModalVisible,
} = nhoSlice.actions;

// Selectors for centralized errors
export const selectCentralizedErrors = (state: any) => state.nho.centralizedErrors;
export const selectIsErrorModalVisible = (state: any) => state.nho.isErrorModalVisible;

export default nhoSlice.reducer;
